import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { useContext } from "react";
import { AuthProvider, AuthContext } from "./context/AuthContext";

import LandingPage from "./pages/LandingPage";
import Signup from "./pages/Signup";
import AdminLogin from "./pages/AdminLogin";
import EmployeeLogin from "./pages/EmployeeLogin";
import AdminDashboard from "./pages/AdminDashboard";
import ClientSection from "./pages/ClientsSection";
import AddClient from "./pages/AddClient";
import ClientDetails from "./pages/ClientDetails";
import EmployeesSection from "./pages/EmployeesSection";
import AddEmployee from "./pages/AddEmployee";
import EmployeeDetails from "./pages/EmployeeDetails";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import SelectOrganization from "./pages/SelectOrganization";
import VersionHistory from "./pages/VersionHistory";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";


// =====================================================
// 🔐 PROTECTED ROUTE
// =====================================================
function ProtectedRoute({ children, allowedRoles }) {

  const { user, role, loading, orgSlug: contextSlug } = useContext(AuthContext);
  const { orgSlug: urlSlug } = useParams();

  // ⏳ Loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }

  // ❌ Not logged in
  if (!user) {
    if (allowedRoles?.includes("admin")) {
      return <Navigate to="/admin-login" replace />;
    }

    if (allowedRoles?.includes("employee")) {
      return <Navigate to="/employee-login" replace />;
    }

    return <Navigate to="/" replace />;
  }

  // ⏳ Role not ready
  if (!role) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }

  // 🔥 SUPER ADMIN → full access
  if (role === "super_admin") {
    return children;
  }

  // 🚫 Role restriction
  if (allowedRoles && !allowedRoles.includes(role)) {
    return <Navigate to="/" replace />;
  }

  // 🔁 Org slug mismatch fix
  if (urlSlug && contextSlug && urlSlug !== contextSlug) {
    return <Navigate to={`/${contextSlug}/${role}-dashboard`} replace />;
  }

  return children;
}


// =====================================================
// 🔁 ROLE REDIRECT
// =====================================================
function RoleRedirect() {
  const { role, orgSlug, loading } = useContext(AuthContext);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }

  if (!role) return null;

  // =====================================================
  // 🔥 SUPER ADMIN LOGIC (UPDATED)
  // =====================================================

  if (role === "super_admin") {
    // ✅ If has org → go to admin dashboard
    if (orgSlug) {
      return <Navigate to={`/${orgSlug}/admin-dashboard`} replace />;
    }

    // ✅ No org → go to super admin panel
    return <Navigate to="/super-admin" replace />;
  }

  // =====================================================
  // NORMAL USERS
  // =====================================================

  const creatingOrg = localStorage.getItem("creatingOrg");

if (
  (role === "pending" || role === "multi_org") &&
  !creatingOrg
) {
  return <Navigate to="/select-organization" replace />;
}
  if (role === "admin" && orgSlug) {
    return <Navigate to={`/${orgSlug}/admin-dashboard`} replace />;
  }

  if (role === "employee" && orgSlug) {
    return <Navigate to={`/${orgSlug}/employee-dashboard`} replace />;
  }

  return <Navigate to="/" replace />;
}


// =====================================================
// 🚀 ROUTES
// =====================================================
function AppRoutes() {
  return (
    <Routes>

      <Route path="/" element={<LandingPage />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/admin-login" element={<AdminLogin />} />
      <Route path="/employee-login" element={<EmployeeLogin />} />
      <Route path="/redirect" element={<RoleRedirect />} />

      {/* 🔥 SELECT ORG */}
      <Route
        path="/select-organization"
        element={
          <ProtectedRoute>
            <SelectOrganization />
          </ProtectedRoute>
        }
      />

      {/* 🔥 SUPER ADMIN */}
      <Route
        path="/super-admin"
        element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <SuperAdminDashboard />
          </ProtectedRoute>
        }
      />

      {/* ================= ORG ROUTES ================= */}
      <Route path="/:orgSlug">

        {/* ADMIN */}
        <Route
          path="admin-dashboard"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        {/* EMPLOYEE */}
        <Route
          path="employee-dashboard"
          element={
            <ProtectedRoute allowedRoles={["employee"]}>
              <EmployeeDashboard />
            </ProtectedRoute>
          }
        />

        {/* PROJECT HISTORY */}
        <Route
          path="project-history/:projectId"
          element={
            <ProtectedRoute allowedRoles={["admin", "employee"]}>
              <VersionHistory />
            </ProtectedRoute>
          }
        />

        {/* EMPLOYEES */}
        <Route path="employees">

          <Route
            index
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <EmployeesSection />
              </ProtectedRoute>
            }
          />

          <Route
            path="add"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AddEmployee />
              </ProtectedRoute>
            }
          />

          <Route
            path="edit/:id"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AddEmployee />
              </ProtectedRoute>
            }
          />

          <Route
            path=":id"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <EmployeeDetails />
              </ProtectedRoute>
            }
          />

        </Route>

        {/* CLIENTS */}
        <Route path="clients">

          <Route
            index
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <ClientSection />
              </ProtectedRoute>
            }
          />

          <Route
            path="add"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AddClient />
              </ProtectedRoute>
            }
          />

          <Route
            path=":id"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <ClientDetails />
              </ProtectedRoute>
            }
          />

        </Route>

      </Route>

      {/* FALLBACK */}
      <Route path="*" element={<Navigate to="/" replace />} />

    </Routes>
  );
}


// =====================================================
// 🌐 APP ROOT
// =====================================================
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;